import { get } from 'lodash';
import { ReactNode, useContext, createElement, createContext, useMemo } from 'react';
import { DashboardContext, DashboardContextType } from '../../../hooks';
import { getViewElementParts, isTemplate, processTemplateProps } from '../../../utils';
import { TemplateRenderer } from '.';
import { logger } from '../../../utils';
import { DashboardJson } from '../../models';
import { isExpressionError, ExpressionError } from './utils/isExpressionError';
import { utils } from '@appd/uik';

interface ViewRendererProps {
  localTemplates?: ReadonlyArray<DashboardJson>;
  importedTemplates?: ReadonlyArray<DashboardJson>;
  view: unknown;
  name?: string;
}

export const ViewRenderer = ({
  localTemplates: rawLocalTemplates,
  importedTemplates: rawImportedTemplates,
  view,
  name = 'root',
}: ViewRendererProps) => {
  const inherited = useContext(ViewRenderer.CONTEXT);

  const context = useMemo(
    () => ({
      localTemplates: [...inherited.localTemplates, ...(rawLocalTemplates ?? [])],
      importedTemplates: [...inherited.importedTemplates, ...(rawImportedTemplates ?? [])],
    }),
    [rawLocalTemplates, inherited.localTemplates, rawImportedTemplates, inherited.importedTemplates],
  );

  const { ComponentRegistry, TemplateRegistry } = useContext(DashboardContext);
  logger.log('ViewRenderer', {
    view,
    name,
    ComponentRegistry,
    TemplateRegistry,
  });

  return (
    <ViewRenderer.CONTEXT.Provider value={context}>
      {ViewRenderer.generateView({
        localTemplates: context.localTemplates,
        importedTemplates: context.importedTemplates,
        ComponentRegistry,
        TemplateRegistry,
        view,
        name,
      })}
    </ViewRenderer.CONTEXT.Provider>
  );
};

ViewRenderer.CONTEXT = createContext({
  /** @deprecated Temporarily added for `halfHeartedAttemptToSupportSlots`. */
  localTemplates: [] as ReadonlyArray<DashboardJson>,
  /** @deprecated Temporarily added for `halfHeartedAttemptToSupportSlots`. */
  importedTemplates: [] as ReadonlyArray<DashboardJson>,
});

ViewRenderer.generateView = ({
  localTemplates,
  importedTemplates,
  view,
  name,
  ComponentRegistry,
  TemplateRegistry,
}: {
  localTemplates: ReadonlyArray<DashboardJson>;
  importedTemplates: ReadonlyArray<DashboardJson>;
  view: string | number | boolean | Record<string, unknown>[] | unknown | ExpressionError;
  name: string;
} & Pick<DashboardContextType, 'ComponentRegistry' | 'TemplateRegistry'>): string | number | boolean | ReactNode => {
  if (isExpressionError(view)) {
    const {
      error: { name: errorName, message: errorMessage },
    } = view;
    throw new Error(`${errorName}: "${errorMessage}" occurred in ${name}.`);
  }

  if (typeof view === 'string' || typeof view === 'number' || typeof view === 'boolean' || view === null) {
    return view;
  }
  return (view as Record<string, unknown>[])?.map((viewElement: Record<string, unknown>, i: number) => {
    const parent = 'root';
    const { tag, props, children } = getViewElementParts(viewElement);
    logger.log('rendering view', name, tag, props?.condition);
    if (!tag) {
      return null;
    }
    const newProps = processTemplateProps(props, name, parent, tag, i, children);
    if (isTemplate(TemplateRegistry, tag) || isTemplate(localTemplates, tag) || isTemplate(importedTemplates, tag)) {
      return <TemplateRenderer name={tag} key={newProps.key} props={newProps} childrenDef={children} />;
    }

    const Component = get(ComponentRegistry, tag);

    return createElement(
      Component ?? tag,
      newProps,
      ViewRenderer.generateView({
        localTemplates,
        importedTemplates,
        ComponentRegistry,
        TemplateRegistry,
        view: children,
        name: tag,
      }),
    );
  });
};

/** @deprecated For temporary use in `src/modules/components/index.tsx` until supported in `ViewRenderer` directly. */
ViewRenderer.halfHeartedAttemptToSupportSlots = (
  tag: string,
  Component: (props: any) => JSX.Element,
  slots: string[],
): typeof Component => {
  const slotsSet = new Set(slots);

  return utils.forwardRef(
    utils.rename(tag, (props, ref) => {
      const { localTemplates, importedTemplates } = useContext(ViewRenderer.CONTEXT);

      const { ComponentRegistry, TemplateRegistry } = useContext(DashboardContext);

      return (
        <Component
          ref={ref}
          {...utils.transform(props).by.entry(
            ([key, value]) =>
              [
                key,
                value && Array.isArray(value) && slotsSet.has(key)
                  ? ViewRenderer.generateView({
                      ComponentRegistry,
                      TemplateRegistry,
                      localTemplates,
                      importedTemplates,
                      view: value,
                      name: tag,
                    })
                  : value,
              ] as const,
          )}
        />
      );
    }),
  );
};
